import React, { useRef, useState } from "react";
import { toast } from "react-toastify";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";

import { uploadFile, postRightworkAPI } from "../requests/utilities/requests";
import {
  API_attach_board_card_file,
  API_remove_board_card_file,
} from "../../../constants";

const BoardCardAttachments = ({ card, storeId, companyId, onCardUpdated }) => {
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const files = card.file_uploads || [];

  const handleFileSelect = async (e) => {
    const file = e.target.files && e.target.files[0];
    // Reset the input so selecting the same file again still fires onChange.
    e.target.value = "";
    if (!file) return;

    setUploading(true);
    try {
      const payload = await uploadFile(companyId, file, "board_card");
      const fileUuid = payload && payload.uuid;
      if (!fileUuid) throw new Error("Upload did not return a file");
      const res = await postRightworkAPI(API_attach_board_card_file, {
        store_id: storeId,
        board_card_uuid: card.uuid,
        file_upload_uuid: fileUuid,
      });
      const updated = res.data?.redux?.payload?.board_card;
      if (updated) onCardUpdated(updated);
    } catch (error) {
      toast.error("Failed to upload attachment");
    } finally {
      setUploading(false);
    }
  };

  const removeFile = async (fileUuid) => {
    try {
      const res = await postRightworkAPI(API_remove_board_card_file, {
        store_id: storeId,
        board_card_uuid: card.uuid,
        file_upload_uuid: fileUuid,
      });
      const updated = res.data?.redux?.payload?.board_card;
      if (updated) onCardUpdated(updated);
    } catch (error) {
      toast.error("Failed to remove attachment");
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-1.5">
        <p className="text-xs font-semibold text-slate-500">ATTACHMENTS</p>
        <button
          className="flex items-center gap-1 text-xs font-medium text-violet-700 hover:text-violet-800 disabled:text-slate-400"
          onClick={() => fileInputRef.current && fileInputRef.current.click()}
          disabled={uploading}
        >
          <AddIcon style={{ fontSize: 15 }} />
          {uploading ? "Uploading…" : "Add"}
        </button>
      </div>
      <input
        ref={fileInputRef}
        type="file"
        className="hidden"
        onChange={handleFileSelect}
      />

      {files.length === 0 ? (
        <p className="text-xs text-slate-400">No attachments.</p>
      ) : (
        <div className="space-y-1.5">
          {files.map((f) => (
            <div
              key={f.uuid}
              className="group flex items-center gap-2 border border-slate-200 rounded-lg px-2.5 py-2"
            >
              <InsertDriveFileIcon
                style={{ fontSize: 18 }}
                className="text-slate-400 shrink-0"
              />
              <a
                href={f.presigned_url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 min-w-0 text-sm text-slate-700 hover:text-violet-700 truncate"
              >
                {f.displayName || f.name}
              </a>
              {f.fileSize && (
                <span className="text-xs text-slate-400 shrink-0">{f.fileSize}</span>
              )}
              <button
                className="text-slate-400 hover:text-red-600 opacity-0 group-hover:opacity-100 shrink-0"
                onClick={() => removeFile(f.uuid)}
                title="Remove attachment"
              >
                <CloseIcon style={{ fontSize: 16 }} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default BoardCardAttachments;
